import { reactive, onUnmounted } from "vue";
import { useNotification } from "@ui-kit";
import { wtfirApi_v2, itemGetZoneApi, WTFIRInputType, ResponseCode } from "@src/api";
import { getNotFoundItemIds } from "../model";

type ScanState = {
  // время в мс
  readonly batch_time_ms: number;
  // размер буфера
  readonly batch_size: number;
  // массив всех отсканированных данных
  data: Set<string>;
  // массив отсканированных данных для отправки на бэк
  buffer: string[];
  // id таймера
  timer_id: ReturnType<typeof setTimeout> | null;
  // id зоны
  zone_id: string | null;
};

const scan_state: ScanState = {
  batch_size: 50,
  batch_time_ms: 300,
  data: new Set<string>(),
  buffer: [],
  timer_id: null,
  zone_id: null,
};

const state = reactive<{
  found_item_ids: string[];
  not_binding_item_ids: string[];
  // id товара, который находится вне зоны при единичном сканировании
  item_id_without_zone_single_scan: string | null;
  // id товара, который находится в зоне при единичном сканировании
  item_id_in_zone_single_scan: string | null;
}>({
  found_item_ids: [],
  not_binding_item_ids: [],
  item_id_without_zone_single_scan: null,
  item_id_in_zone_single_scan: null,
});

async function sendBatch(data: string[]): Promise<string[]> {
  const response = await wtfirApi_v2({ input_type: WTFIRInputType.rfid, data });
  let item_ids = [];

  if (response.code === ResponseCode.OK) {
    // TODO: вынести в модель
    item_ids = response.result.map((item) => item.item[0]).filter(Boolean);
  }

  return item_ids;
}

async function loadItemLinks(item_ids: string[]) {
  const map_zone_seria: Record<string, string[]> = {};
  let map_item_seria: Record<string, string> = {};
  const item_ids_in_all_zone = new Set<string>();

  try {
    const response = await itemGetZoneApi({ item_id: item_ids });

    if (response.code === ResponseCode.OK) {
      map_item_seria = { ...response.map_item_seria };

      response.links.forEach((item) => {
        item_ids_in_all_zone.add(item.item_id);

        if (!map_zone_seria[item.zone_id]) {
          map_zone_seria[item.zone_id] = [];
        }

        map_zone_seria[item.zone_id].push(item.item_id);
      });
    }
  } catch {}

  return { map_zone_seria, map_item_seria, item_ids_in_all_zone };
}

/** Отправляет накопленный батч на бэк и очищает буфер. */
async function processingBatch() {
  if (scan_state.timer_id !== null) {
    clearTimeout(scan_state.timer_id);
    scan_state.timer_id = null;
  }

  if (scan_state.buffer.length === 0) return;

  const copy_buffer = [...scan_state.buffer];
  scan_state.buffer.length = 0;
  const item_ids = await sendBatch(copy_buffer);

  if (item_ids.length === 0) return;

  const { map_item_seria, map_zone_seria, item_ids_in_all_zone } = await loadItemLinks(item_ids);

  const item_ids_in_zone: string[] = [];
  const item_ids_without_zone: string[] = [];

  const seria_ids_in_zone = map_zone_seria[scan_state.zone_id] ?? [];
  if (!seria_ids_in_zone) return;

  item_ids.forEach((item_id) => {
    const seria_id = map_item_seria[item_id];

    if (!seria_id) return;

    if (!item_ids_in_all_zone.has(seria_id)) {
      item_ids_without_zone.push(seria_id);
    } else if (seria_ids_in_zone.includes(seria_id)) {
      item_ids_in_zone.push(seria_id);
    }
  });

  state.found_item_ids = Array.from(new Set([...state.found_item_ids, ...item_ids_in_zone]));
  state.not_binding_item_ids = Array.from(new Set([...state.not_binding_item_ids, ...item_ids_without_zone]));
}

/**
 * Логика батчинга сканов RFID для проверки привязки.
 * - Только уникальные значения: повторные из подписки не обрабатываются.
 * - Отправка на бэк: через 300 мс после первого в батче или при 50 шт.
 * - После отправки подписка не снимается, слушаем дальше.
 */
export function useCheckBindingBatch() {
  const notification = useNotification();

  onUnmounted(() => {
    if (scan_state.timer_id !== null) {
      clearTimeout(scan_state.timer_id);
      scan_state.timer_id = null;
    }

    scan_state.data.clear();
    scan_state.buffer.length = 0;
    scan_state.zone_id = null;

    state.found_item_ids = [];
    state.not_binding_item_ids = [];
  });

  function processingScanData(value: string) {
    const sliced_value = value.slice(3);

    if (scan_state.data.has(sliced_value)) return;

    scan_state.data.add(sliced_value);
    scan_state.buffer.push(value);

    if (scan_state.buffer.length >= scan_state.batch_size) {
      processingBatch();
    } else if (scan_state.timer_id === null) {
      scan_state.timer_id = setTimeout(processingBatch, scan_state.batch_time_ms);
    }
  }

  async function processingSingleScanData(value: string) {
    const sliced_value = value.slice(3);

    if (!scan_state.data.has(sliced_value)) {
      scan_state.data.add(sliced_value);
    }

    const item_ids = await sendBatch([value]);

    if (item_ids.length === 0) {
      notification.show({ title: "Товар не найден", type: "error" });
      return;
    }

    const { map_item_seria, map_zone_seria, item_ids_in_all_zone } = await loadItemLinks(item_ids);
    const seria_ids_in_zone = map_zone_seria[scan_state.zone_id] ?? [];

    const item_id = item_ids[0];
    const seria_id = map_item_seria[item_id];

    if (!seria_id) return;

    const in_zone = seria_ids_in_zone.includes(seria_id);
    const without_zone = !item_ids_in_all_zone.has(seria_id);

    if (without_zone) {
      state.item_id_without_zone_single_scan = seria_id;
    } else if (in_zone) {
      if (state.found_item_ids.includes(seria_id)) {
        notification.show({ title: "Цветомодель уже в списке", type: "success" });
      } else {
        state.found_item_ids = [...state.found_item_ids, seria_id];
      }
    } else {
      state.item_id_in_zone_single_scan = seria_id;
    }
  }

  function setZoneId(zone_id: string) {
    scan_state.zone_id = zone_id;
  }

  function clearZoneId() {
    scan_state.zone_id = null;
  }

  function moveLinkedToFound(item_ids_to_move: string[]) {
    state.not_binding_item_ids = state.not_binding_item_ids.filter((id) => !item_ids_to_move.includes(id));
    state.found_item_ids = [...state.found_item_ids, ...item_ids_to_move];
  }

  function clearItemIdSingleScan() {
    state.item_id_without_zone_single_scan = null;
    state.item_id_in_zone_single_scan = null;
  }

  function moveItemIdSingleScanToFound() {
    if (state.item_id_without_zone_single_scan) {
      moveLinkedToFound([state.item_id_without_zone_single_scan]);
    } else if (state.item_id_in_zone_single_scan) {
      moveLinkedToFound([state.item_id_in_zone_single_scan]);
    }

    clearItemIdSingleScan();
  }

  function deleteItemFromFoundById(item_id: string) {
    state.found_item_ids = state.found_item_ids.filter((id) => id !== item_id);
  }

  return {
    state,
    processingScanData,
    processingSingleScanData,
    getNotFoundItemIds,
    setZoneId,
    clearZoneId,
    moveLinkedToFound,
    clearItemIdSingleScan,
    moveItemIdSingleScanToFound,
    deleteItemFromFoundById,
  };
}
